<template>
  <div class="project-wayline-wrapper height-100">
    <a-spin :spinning="loading" :delay="300" tip="正在下载…" size="large">
    <div class="wayline-header">
      <a-row>
        <a-col :span="1"></a-col>
        <a-col :span="15">航线库</a-col>
        <a-col :span="8" v-if="importVisible" class="flex-row flex-justify-end flex-align-center">
          <a-upload
            name="file"
            :multiple="false"
            :before-upload="beforeUpload"
            :show-upload-list="false"
            :customRequest="uploadFile"
          >
            <a-button type="text" class="import-btn">
              <SelectOutlined />
            </a-button>
          </a-upload>
        </a-col>
      </a-row>
    </div>
    <div :style="{ height : height + 'px'}" class="scrollbar">
      <div id="data" class="height-100 uranus-scrollbar" v-if="waylinesData.data.length !== 0" @scroll="onScroll">
        <div v-for="wayline in waylinesData.data" :key="wayline.id">
          <div
            class="wayline-panel"
            :class="{ 'wayline-panel--selected': selectedWaylineId === wayline.id }"
            style="padding-top: 5px;"
            role="button"
            tabindex="0"
            @click="selectRoute(wayline)"
            @keydown.enter.prevent="selectRoute(wayline)"
          >
            <div class="title">
              <a-tooltip :title="wayline.name">
                <div class="pr10" style="width: 120px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.name }}</div>
              </a-tooltip>
              <div class="ml10"><UserOutlined /></div>
              <a-tooltip :title="wayline.user_name">
                <div class="ml5 pr10" style="width: 80px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.user_name }}</div>
              </a-tooltip>
              <div class="fz20">
                <a-dropdown>
                  <a class="dropdown-trigger">
                    <EllipsisOutlined />
                  </a>
                  <template #overlay>
                    <a-menu theme="dark" class="more" style="background: #3c3c3c;">
                      <a-menu-item @click="downloadWayline(wayline.id, wayline.name)">
                        <span>下载</span>
                      </a-menu-item>
                      <a-menu-item @click="showWaylineTip(wayline.id)">
                        <span>删除</span>
                      </a-menu-item>
                    </a-menu>
                  </template>
                </a-dropdown>
              </div>
            </div>
            <div class="ml10 mt5 wayline-info">
              <span><RocketOutlined /></span>
              <span class="ml5">{{ DEVICE_NAME[wayline.drone_model_key] }}</span>
              <span class="ml10"><CameraFilled style="border-top: 1px solid; padding-top: -3px;" /></span>
              <span class="ml5" v-for="payload in wayline.payload_model_keys" :key="payload.id">
                {{ DEVICE_NAME[payload] }}
              </span>
            </div>
            <div class="mt5 ml10 wayline-meta">
              <span class="mr10">更新于 {{ new Date(wayline.update_time).toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <a-empty :image-style="{ height: '60px', marginTop: '60px' }" />
      </div>
      <a-modal
        v-model:visible="deleteTip"
        width="450px"
        :closable="false"
        :maskClosable="false"
        centered
        ok-text="确定"
        cancel-text="取消"
        :okButtonProps="{ danger: true }"
        @ok="deleteWayline"
        @cancel="deleteTip = false"
      >
          <p class="pt10 pl20" style="height: 50px;">删除后航线文件无法恢复，确定继续吗？</p>
          <template #title>
              <div class="flex-row flex-justify-center">
                  <span>删除航线</span>
              </div>
          </template>
      </a-modal>
    </div>
    </a-spin>
  </div>
</template>

<script lang="ts" setup>
import { reactive } from '@vue/reactivity'
import { message } from 'ant-design-vue'
import { computed, onMounted, ref } from 'vue'
import { deleteWaylineFile, downloadWaylineFile, getWaylineFiles, importKmzFile } from '/@/api/wayline'
import { ELocalStorageKey, ERouterName } from '/@/types'
import { EllipsisOutlined, RocketOutlined, CameraFilled, UserOutlined, SelectOutlined } from '@ant-design/icons-vue'
import { DEVICE_NAME } from '/@/types/device'
import { useMyStore } from '/@/store'
import { WaylineFile } from '/@/types/wayline'
import { downloadFile } from '/@/utils/common'
import { IPage } from '/@/api/http/type'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { load } from '@amap/amap-jsapi-loader'
import { getRoot } from '/@/root'

const loading = ref(false)
const store = useMyStore()

const selectedWaylineId = computed(() => (store.state.waylineInfo as WaylineFile)?.id ?? '')
const pagination :IPage = {
  page: 1,
  total: -1,
  page_size: 10
}

const waylinesData = reactive({
  data: [] as WaylineFile[]
})

const root = getRoot()
const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
const deleteTip = ref(false)
const deleteWaylineId = ref<string>('')
const canRefresh = ref(true)
const importVisible = ref<boolean>(root.$router.currentRoute.value.name === ERouterName.WAYLINE)
const height = ref()

onMounted(() => {
  const scrollEl = document.getElementsByClassName('scrollbar').item(0)
  const parent = scrollEl?.parentNode as HTMLDivElement | undefined
  const header = parent?.firstElementChild as HTMLElement | undefined
  height.value = header
    ? document.body.clientHeight - header.clientHeight
    : document.body.clientHeight
  getWaylines()

  const key = setInterval(() => {
    const data = document.getElementById('data')?.lastElementChild as HTMLDivElement
    if (pagination.total === 0 || Math.ceil(pagination.total / pagination.page_size) <= pagination.page || height.value <= data?.clientHeight + data?.offsetTop) {
      clearInterval(key)
      return
    }
    pagination.page++
    getWaylines()
  }, 1000)
})

/** 首页列表加载后：无选中或选中已不在列表时，默认选中第一条，便于右侧地图立即预览 */
function syncDefaultWaylineSelection () {
  const list = waylinesData.data
  if (!list.length) {
    return
  }
  const current = store.state.waylineInfo as WaylineFile
  const currentId = current?.id
  if (currentId && list.some(w => w.id === currentId)) {
    return
  }
  store.commit('SET_SELECT_WAYLINE_INFO', list[0])
}

function getWaylines () {
  if (!canRefresh.value) {
    return
  }
  canRefresh.value = false
  getWaylineFiles(workspaceId, {
    page: pagination.page,
    page_size: pagination.page_size,
    order_by: 'update_time desc'
  }).then(res => {
    if (res.code !== 0) {
      return
    }
    waylinesData.data = [...waylinesData.data, ...res.data.list]
    pagination.total = res.data.pagination.total
    pagination.page = res.data.pagination.page
    const page = res.data.pagination?.page ?? 1
    if (page === 1) {
      syncDefaultWaylineSelection()
    }
  }).finally(() => {
    canRefresh.value = true
  })
}

function showWaylineTip (waylineId: string) {
  deleteWaylineId.value = waylineId
  deleteTip.value = true
}

function deleteWayline () {
  deleteWaylineFile(workspaceId, deleteWaylineId.value).then(res => {
    if (res.code === 0) {
      message.success('航线文件已删除')
    }
    deleteWaylineId.value = ''
    deleteTip.value = false
    pagination.total = 0
    pagination.page = 1
    waylinesData.data = []
    getWaylines()
  })
}

function downloadWayline (waylineId: string, fileName: string) {
  loading.value = true
  downloadWaylineFile(workspaceId, waylineId).then(res => {
    if (!res) {
      return
    }
    const data = new Blob([res], { type: 'application/zip' })
    downloadFile(data, fileName + '.kmz')
  }).finally(() => {
    loading.value = false
  })
}

function selectRoute (wayline: WaylineFile) {
  store.commit('SET_SELECT_WAYLINE_INFO', wayline)
}

function onScroll (e: any) {
  const element = e.srcElement
  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 5 && Math.ceil(pagination.total / pagination.page_size) > pagination.page && canRefresh.value) {
    pagination.page++
    getWaylines()
  }
}

interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  response?: string;
  url?: string;
}

interface FileInfo {
  file: FileItem;
  fileList: FileItem[];
}
const fileList = ref<FileItem[]>([])

function beforeUpload (file: FileItem) {
  fileList.value = [file]
  loading.value = true
  return true
}
const uploadFile = async () => {
  fileList.value.forEach(async (file: FileItem) => {
    const fileData = new FormData()
    fileData.append('file', file, file.name)
    await importKmzFile(workspaceId, fileData).then((res) => {
      if (res.code === 0) {
        message.success(`${file.name} 上传成功`)
        canRefresh.value = true
        pagination.total = 0
        pagination.page = 1
        waylinesData.data = []
        getWaylines()
      }
    }).finally(() => {
      loading.value = false
      fileList.value = []
    })
  })
}

</script>

<style lang="scss" scoped>
@import '/@/styles/variables.scss';

.wayline-panel {
  background: #fff;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  height: 90px;
  width: 95%;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgba($primary, 0.15);
  box-shadow: 0 1px 3px rgba($primary, 0.08);
  transition: all 0.2s ease;

  &:hover {
    background: var(--cm-surface-blue);
    border-color: rgba($primary, 0.25);
  }

  &.wayline-panel--selected {
    position: relative;
    background: var(--cm-surface-blue, #eaf4ff);
    border-color: #91caff;
    color: $primary;
    box-shadow: 0 1px 3px rgba(22, 119, 255, 0.12);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 56%;
      min-height: 22px;
      border-radius: 0 3px 3px 0;
      background: $primary;
    }

    .title {
      color: $primary;
    }

    .wayline-info {
      color: $primary;
      opacity: 0.92;
    }

    .wayline-meta {
      color: rgba($primary, 0.75);
    }

    .dropdown-trigger {
      color: $primary;
    }
  }

  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 30px;
    font-weight: bold;
    margin: 0px 10px 0 10px;
    color: $light-title;
  }

  .dropdown-trigger {
    color: $light-main-text;
  }

  .wayline-info {
    color: $light-main-text;
  }

  .wayline-meta {
    color: $light-auxiliary;
  }
}
.wayline-header {
  height: 50px;
  line-height: 50px;
  border-bottom: 1px solid rgba($primary, 0.2);
  font-weight: 450;
  color: $light-title;

  .import-btn {
    color: $primary;
  }
}

.uranus-scrollbar {
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba($primary, 0.3) transparent;
}
</style>
